import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Sheet, SheetContent, SheetTrigger } from '../components/ui/sheet';
import { ProductCard } from '../components/ProductCard';
import { Search, SlidersHorizontal, X } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Store() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const page = parseInt(searchParams.get('page') || '1');
  const search = searchParams.get('search') || '';
  const categoryId = searchParams.get('category') || '';
  const brand = searchParams.get('brand') || '';
  const sort = searchParams.get('sort') || 'newest';
  const inStock = searchParams.get('in_stock');

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 12, sort };
      if (search) params.search = search;
      if (categoryId) params.category_id = categoryId;
      if (brand) params.brand = brand;
      if (inStock === 'true') params.in_stock = true;
      const res = await axios.get(`${API}/products`, { params });
      setProducts(res.data.products);
      setTotal(res.data.total);
      setPages(res.data.pages);
    } catch (err) { console.error(err); } finally { setLoading(false); }
  }, [page, search, categoryId, brand, sort, inStock]);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);
  useEffect(() => {
    axios.get(`${API}/categories`).then(r => setCategories(r.data)).catch(() => {});
    axios.get(`${API}/brands`).then(r => setBrands(r.data)).catch(() => {});
  }, []);

  const updateParam = (key, value) => {
    const params = new URLSearchParams(searchParams);
    if (value) params.set(key, value); else params.delete(key);
    if (key !== 'page') params.set('page', '1');
    setSearchParams(params);
  };

  const clearFilters = () => setSearchParams({});

  const parentCats = categories.filter(c => !c.parent_id);
  const activeFilters = [categoryId, brand, inStock].filter(Boolean).length;

  const FilterContent = () => (
    <div className="space-y-6">
      <div>
        <h4 className="font-display font-semibold text-sm mb-3">Categorías</h4>
        <div className="space-y-1">
          <button onClick={() => updateParam('category', '')} className={`block w-full text-left px-2 py-1.5 rounded text-sm transition-colors duration-150 ${!categoryId ? 'bg-secondary font-medium' : 'text-muted-foreground hover:bg-secondary/50'}`}>Todas</button>
          {parentCats.map(cat => (
            <div key={cat.id}>
              <button onClick={() => updateParam('category', cat.id)} className={`block w-full text-left px-2 py-1.5 rounded text-sm transition-colors duration-150 ${categoryId === cat.id ? 'bg-secondary font-medium' : 'text-muted-foreground hover:bg-secondary/50'}`}>{cat.name}</button>
              {categories.filter(s => s.parent_id === cat.id).map(sub => (
                <button key={sub.id} onClick={() => updateParam('category', sub.id)} className={`block w-full text-left px-2 py-1.5 pl-6 rounded text-sm transition-colors duration-150 ${categoryId === sub.id ? 'bg-secondary font-medium' : 'text-muted-foreground hover:bg-secondary/50'}`}>{sub.name}</button>
              ))}
            </div>
          ))}
        </div>
      </div>
      <div>
        <h4 className="font-display font-semibold text-sm mb-3">Marca</h4>
        <div className="space-y-1">
          <button onClick={() => updateParam('brand', '')} className={`block w-full text-left px-2 py-1.5 rounded text-sm transition-colors duration-150 ${!brand ? 'bg-secondary font-medium' : 'text-muted-foreground hover:bg-secondary/50'}`}>Todas</button>
          {brands.map(b => (
            <button key={b} onClick={() => updateParam('brand', b)} className={`block w-full text-left px-2 py-1.5 rounded text-sm transition-colors duration-150 ${brand === b ? 'bg-secondary font-medium' : 'text-muted-foreground hover:bg-secondary/50'}`}>{b}</button>
          ))}
        </div>
      </div>
      <div>
        <h4 className="font-display font-semibold text-sm mb-3">Disponibilidad</h4>
        <button onClick={() => updateParam('in_stock', inStock === 'true' ? '' : 'true')} className={`px-2 py-1.5 rounded text-sm transition-colors duration-150 ${inStock === 'true' ? 'bg-secondary font-medium' : 'text-muted-foreground hover:bg-secondary/50'}`}>Solo en stock</button>
      </div>
      {activeFilters > 0 && (
        <Button variant="ghost" size="sm" onClick={clearFilters} className="w-full gap-2"><X className="h-3 w-3" /> Limpiar filtros</Button>
      )}
    </div>
  );

  return (
    <div className="py-8 md:py-12">
      <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h1 className="text-3xl sm:text-4xl font-display font-semibold tracking-tight mb-2">Tienda en Línea</h1>
          <p className="text-muted-foreground">Catálogo de productos KIND TECHNOLOGIES</p>
        </div>

        {/* Toolbar */}
        <div className="flex flex-wrap items-center gap-3 mb-6">
          <div className="relative flex-1 min-w-[200px] max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Buscar productos..."
              value={search}
              onChange={e => updateParam('search', e.target.value)}
              className="pl-9"
              data-testid="catalog-search-input"
            />
          </div>
          <Select value={sort} onValueChange={v => updateParam('sort', v)}>
            <SelectTrigger className="w-[180px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="newest">Más recientes</SelectItem>
              <SelectItem value="price_asc">Precio: menor a mayor</SelectItem>
              <SelectItem value="price_desc">Precio: mayor a menor</SelectItem>
              <SelectItem value="name_asc">Nombre A-Z</SelectItem>
            </SelectContent>
          </Select>
          <Sheet open={filtersOpen} onOpenChange={setFiltersOpen}>
            <SheetTrigger asChild>
              <Button variant="outline" size="sm" className="lg:hidden gap-2">
                <SlidersHorizontal className="h-4 w-4" /> Filtros {activeFilters > 0 && <Badge variant="secondary" className="h-5 px-1.5">{activeFilters}</Badge>}
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-72 p-6">
              <h3 className="font-display font-semibold mb-4">Filtros</h3>
              <FilterContent />
            </SheetContent>
          </Sheet>
          <span className="text-sm text-muted-foreground ml-auto">{total} producto{total !== 1 ? 's' : ''}</span>
        </div>

        <div className="flex gap-8">
          {/* Desktop Filters */}
          <aside className="hidden lg:block w-56 flex-shrink-0">
            <div className="sticky top-20">
              <FilterContent />
            </div>
          </aside>

          {/* Product Grid */}
          <div className="flex-1">
            {loading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="bg-secondary/50 rounded-lg animate-pulse" style={{height: 350}} />
                ))}
              </div>
            ) : products.length === 0 ? (
              <div className="text-center py-16">
                <p className="text-muted-foreground">No se encontraron productos</p>
                <Button variant="outline" onClick={clearFilters} className="mt-4">Limpiar filtros</Button>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {products.map(p => <ProductCard key={p.id} product={p} />)}
                </div>
                {pages > 1 && (
                  <div className="flex justify-center gap-2 mt-8">
                    <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => updateParam('page', String(page - 1))}>Anterior</Button>
                    <span className="flex items-center text-sm text-muted-foreground px-2">Página {page} de {pages}</span>
                    <Button variant="outline" size="sm" disabled={page >= pages} onClick={() => updateParam('page', String(page + 1))}>Siguiente</Button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
